<template>
  <div class="data-record">
    <div v-if="!isMobileView" class="customer row row--hover box box--default">
      <div class="row__data row__data--small row__data--image" @click="goToCustomerFullView($event)">
        <img v-if="customerThumbnail" :src="customerThumbnail" :alt="customerName" />
        <div v-else class="thumbnail thumbnail--customer" :style="color">
          {{ parseInitials(customer.first_name, customer.last_name) }}
        </div>
      </div>
      <div class="row__data row__data--name row__data--large" @click="goToCustomerFullView($event)">
        {{ customerName }}
      </div>
      <div class="row__data row__data--contact row__data--medium">
        <a class="row__phone icon__phone" :href="customerPhone ? `tel:${customerPhone}` : 'javascript:void(0)'"
          :class="{ 'icon__phone--active': customerPhone }"></a>
        <a class="row__mail icon__mail" :href="customerEmail ? `mailto:${customerEmail}` : 'javascript:void(0)'"
          :class="{ 'icon__mail--active': customerEmail }"></a>
      </div>
      <div class="row__data row__data--address row__data--large" @click="goToCustomerFullView($event)">
        {{ `${customerStreet} ${customerAddressNumber}` }}
      </div>
      <div class="row__data row__data--zip row__data--small" @click="goToCustomerFullView($event)">
        {{ customerZip }}
      </div>
      <div class="row__data row__data--city row__data--medium" @click="goToCustomerFullView($event)">
        {{ customerCity }}
      </div>
      <div class="row__data row__data--date row__data--medium" @click="goToCustomerFullView($event)">
        {{ createdAt }}
      </div>
      <div class="row__data row__data--date row__data--medium" @click="goToCustomerFullView($event)">
        {{ updatedAt }}
      </div>
      <div class="row__data row__data--type row__data--large">
        <VSelect v-if="insurances" :options="insurances" :getOptionLabel="(option) => option.title || ''"
          class="select select--default customer__dropdown" v-model="selectedInsurance" />
        <span v-else>-</span>
      </div>
      <div class="row__data row__data--company row__data--large">
        {{ customerTeam }}
      </div>
      <div v-if="canEditCustomer || canDeleteCustomer" class="row__data row__data--action row__data--small"
        @click.self="showTableActionsDropdown = !showTableActionsDropdown">
        <RowActionDropdown v-if="showTableActionsDropdown" @toggleDropdown="showTableActionsDropdown = $event"
          :onEdit="canEditCustomer ? editCustomer : null" :onDelete="canDeleteCustomer ? deleteCustomer : null" />
      </div>
    </div>

    <article v-if="isMobileView" class="data-card box box--default">
      <header class="data-card__header">
        <button type="button" class="data-card__identity" @click="goToCustomerFullView($event)">
          <span class="data-card__avatar">
            <img v-if="customerThumbnail" :src="customerThumbnail" :alt="customerName" />
            <span v-else class="thumbnail thumbnail--customer" :style="color">
              {{ parseInitials(customer.first_name, customer.last_name) }}
            </span>
          </span>
          <span class="data-card__name">{{ customerName }}</span>
        </button>

        <div v-if="canEditCustomer || canDeleteCustomer" class="data-card__action row__data--action" role="button" tabindex="0"
          :aria-label="`${$t('buttons.editBtn')} / ${$t('buttons.deleteBtn')}`"
          @click.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.enter.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.space.prevent.self="showCardActionsDropdown = !showCardActionsDropdown">
          <RowActionDropdown v-if="showCardActionsDropdown" @toggleDropdown="showCardActionsDropdown = $event"
            :onEdit="canEditCustomer ? editCustomer : null" :onDelete="canDeleteCustomer ? deleteCustomer : null" />
        </div>
      </header>

      <div v-if="customerPhone || customerEmail" class="data-card__contacts">
        <a v-if="customerPhone" class="data-card__contact" :href="`tel:${customerPhone}`">
          <span class="icon__phone--active"></span>
          <span>{{ customerPhone }}</span>
        </a>
        <a v-if="customerEmail" class="data-card__contact" :href="`mailto:${customerEmail}`">
          <span class="icon__mail--active"></span>
          <span>{{ customerEmail }}</span>
        </a>
      </div>

      <dl class="data-card__details">
        <div class="data-card__detail" @click="goToCustomerFullView($event)">
          <dt>{{ $t('customers.tableHeader.address') }}</dt>
          <dd>{{ `${customerStreet} ${customerAddressNumber}` }}</dd>
        </div>
        <div class="data-card__detail" @click="goToCustomerFullView($event)">
          <dt>{{ $t('customers.tableHeader.city') }}</dt>
          <dd>{{ customerZip }} {{ customerCity }}</dd>
        </div>
        <div class="data-card__detail data-card__detail-full">
          <dt>{{ $t('customers.tableHeader.team') }}</dt>
          <dd>{{ customerTeam }}</dd>
        </div>
        <div v-if="insurances" class="data-card__detail data-card__detail--insurance">
          <dt>{{ $t('customers.tableHeader.insurance') }}</dt>
          <dd>
            <VSelect :options="insurances" :getOptionLabel="(option) => option.title || ''"
              class="select select--default customer__dropdown" v-model="selectedInsurance" />
          </dd>
        </div>
        <div class="data-card__detail">
          <dt>{{ $t('customers.tableHeader.createdAt') }}</dt>
          <dd>{{ createdAt }}</dd>
        </div>
        <div class="data-card__detail">
          <dt>{{ $t('customers.tableHeader.updatedAt') }}</dt>
          <dd>{{ updatedAt }}</dd>
        </div>
      </dl>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { parseCustomerName, parseInitials } from "@/helpers/entityParsers";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import { extractEntityTeamIds } from "@/helpers/teamAuthorization";
import RowActionDropdown from "@/components/RowActionDropdown";
import statics from "@/statics";

defineOptions({
  name: "Customer"
});

const props = defineProps({
  customer: {
    required: true,
    type: Object,
  },
});

const store = useStore();
const router = useRouter();

// State
const showTableActionsDropdown = ref(false);
const showCardActionsDropdown = ref(false);
const selectedInsurance = ref({});
const selectedCompany = ref({});
const isMobileView = ref(false);

const updateMobileView = () => {
  isMobileView.value = window.innerWidth <= 900;
};

onMounted(() => {
  updateMobileView();
  window.addEventListener("resize", updateMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateMobileView);
});

// Computed Properties 
const customerName = computed(() => parseCustomerName(props.customer));

const customerStreet = computed(() =>
  props.customer.customer_addresses?.length ? props.customer.customer_addresses[0].street : statics.empty
);

const customerAddressNumber = computed(() =>
  props.customer.customer_addresses?.length ? props.customer.customer_addresses[0].number || "" : statics.empty
);

const customerZip = computed(() =>
  props.customer.customer_addresses?.length ? props.customer.customer_addresses[0].zip : statics.empty
);

const customerTeam = computed(() =>
  props.customer.team ? props.customer.team.name : statics.empty
);
const customerTeamIds = computed(() => extractEntityTeamIds(props.customer));
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canEditCustomer = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.canPerformActionOnEntity("customer_edit", customerTeamIds.value);
});
const canDeleteCustomer = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.canPerformActionOnEntity("customer_delete", customerTeamIds.value);
});
const canShowCustomer = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.canPerformActionOnEntity("customer_show", customerTeamIds.value);
});

const customerCity = computed(() =>
  props.customer.customer_addresses?.length ? props.customer.customer_addresses[0].city : statics.empty
);

const updatedAt = computed(() => formatDisplayDate(props.customer.updated_at) || statics.empty);
const createdAt = computed(() => formatDisplayDate(props.customer.created_at) || statics.empty);

const customerEmail = computed(() =>
  props.customer.customer_emails?.length ? props.customer.customer_emails[0].email : null
);

const customerPhone = computed(() =>
  props.customer.customer_phones?.length ? props.customer.customer_phones[0].phone : null
);

const customerThumbnail = computed(() => props.customer.photo?.thumbnail || null);

const color = computed(() =>
  props.customer.color ? { background: props.customer.color } : { background: "#7f8fa4" }
);

const insurances = computed(() => {
  if (props.customer.policies?.length) {
    const list = props.customer.policies
      .map((p) => p.policy_services || [])
      .flat()
      .filter(Boolean);

    return list.map((service, index) => ({
      id: index,
      title: service.service?.branch_category?.[`name`] || "",
    }));
  }
  return null;
});

const companies = computed(() => {
  if (props.customer.policies?.length) {
    return props.customer.policies.map((p, index) => ({
      id: index,
      name: p.company?.name || null,
    }));
  }
  return null;
});

// Methods
const goToCustomerFullView = (event) => {
  if (!canShowCustomer.value) { 
    return;
  }
 
  const params = { entity: "customer", id: props.customer.id };
  const route = router.resolve({ name: "AccountDetails", params });

  if (event.ctrlKey || event.metaKey) {
    window.open(route.href, '_blank');
  } else {
    router.push({ name: "AccountDetails", params });
  }
};

const editCustomer = () => {
  if (!canEditCustomer.value) { 
    return;
  }
 
  const memberType = props.customer.member_type?.id;
  if (memberType === 1) {
    store.commit("setCompanyIsEdit", true);
    store.commit("setShowAddCompanyModal", true);
    store.commit("setCompanyToEdit", props.customer.id);
  } else if (memberType === 2) {
    store.commit("setPrivateIsEdit", true);
    store.commit("setShowAddPrivateModal", true);
    store.commit("setPrivateToEdit", props.customer.id);
  }
};

const deleteCustomer = () => {
  if (!canDeleteCustomer.value) { 
    return;
  }
 
  store.commit("setDeleteModal", {
    display: true,
    message: `'${props.customer.member_type?.id === 1 ? props.customer.company_name : props.customer.first_name}'`,
    action: store._actions.deleteCustomer,
    id: props.customer.id,
  });
};

// Lifecycle
onMounted(() => {
  updateMobileView();

  if (insurances.value?.length) {
    selectedInsurance.value = { ...insurances.value[0] };
  }
  if (companies.value?.length) {
    selectedCompany.value = { ...companies.value[0] };
  }
});
</script>

<style scoped lang="scss">
.customer__dropdown {
  width: 100%;
  background: 0;
}
</style>
